import { BlogPost } from '@/types/Blog';
import { useFetchBlogPostsWithQuery } from '@/useQuery/queries/blogs';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  ImagesBlogSearchSvg,
  ImagesBlogBlogThumbnailJpeg,
} from '@/assets/images_blog.assets';
import Image from 'next/image';
import LoadingComponent from '../common/LoadingComponent';
import clsx from 'clsx';
import LoadingImage from '../common/LoadingImage';
import { useRouter } from 'next/router';

export default function BlogSearch() {
  const { push } = useRouter();
  const blogsQuery = useFetchBlogPostsWithQuery();

  return (
    <Combobox
      value={blogsQuery.blogSearch}
      onChange={(value) => {
        if (value) {
          push(value);
        }
      }}
      onClose={() => blogsQuery.handleBlogSearch(null)}
    >
      <div className="tw-relative">
        <ComboboxInput
          aria-label="Search for a blog"
          onChange={(event) => blogsQuery.handleBlogSearch(event.target.value)}
          placeholder="Search"
          className="tw-pl-9 tw-border tw-border-solid tw-border-platinum-gray tw-py-2.5 tw-rounded-lg tw-w-[422px] placeholder:tw-text-[#919BA7] placeholder:tw-text-sm placeholder:tw-font-plusJakartaSans placeholder:tw-font-medium
          data-[hover]:tw-border-[#3a81f7] data-[hover]:tw-bg-[#f9fafe]
          data-[focus]:tw-outline-none data-[focus]:tw-ring-2 data-[focus]:tw-ring-[#3a81f7]
          data-[disabled]:tw-bg-[#f5f5f5] data-[disabled]:tw-border-[#e5e5e5] data-[disabled]:tw-text-[#a3a3a3] data-[disabled]:tw-cursor-not-allowed
          data-[error]:tw-border-[#e63946] data-[error]:tw-bg-[#fef2f2] data-[error]:placeholder:tw-text-[#e63946] placeholder:max-nine:tw-text-sm
          max-nine:tw-w-full
          "
        />
        <Image
          src={ImagesBlogSearchSvg}
          alt="Search"
          width={17}
          height={17}
          className="tw-absolute tw-top-2/4 -tw-translate-y-2/4 tw-left-3"
        />
      </div>
      <ComboboxOptions
        anchor="bottom"
        className="tw-mt-2 tw-border-solid tw-border tw-border-gray-200 empty:invisible tw-shadow-lg tw-bg-white tw-rounded-lg tw-overflow-hidden !tw-max-h-80"
      >
        {blogsQuery.isLoading === false && blogsQuery.data.length === 0 && (
          <ComboboxOption
            as="div"
            className="tw-p-4 tw-text-center tw-text-gray-500 tw-w-80 max-nine:tw-text-sm"
            value=""
          >
            No blogs found for your search
          </ComboboxOption>
        )}
        {blogsQuery.isLoading &&
          Array.from({ length: 5 }).map((_, index) => (
            <ComboboxOption
              as={LoadingComponent}
              componentProps={{
                className:
                  'tw-flex tw-items-start tw-p-4 tw-gap-4 tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-b last:tw-border-none tw-border-gray-200 tw-cursor-pointer hover:tw-bg-blue-50',
                disabled: true,
                value: '',
              }}
              key={index}
              loading={true}
              value=""
              disabled={true}
            >
              <LoadingImage className="tw-w-[64px] tw-h-[64px]" loading={true}>
                <></>
              </LoadingImage>
              <div className="tw-w-80 tw-truncate">
                <h3 className="tw-font-semibold tw-text-base tw-text-gray-800 max-nine:tw-text-sm">
                  Loading title...
                </h3>
                <p className="tw-text-sm tw-text-gray-500 max-nine:tw-text-xs">
                  Author • {new Date().toLocaleDateString()}
                </p>
                <p className="tw-text-sm tw-text-gray-600 tw-mt-1 tw-line-clamp-2 max-nine:tw-text-xs">
                  Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                  Cumque molestiae odit magnam quibusdam, quas ratione vitae
                  voluptatem. Saepe, fugiat laudantium?
                </p>
              </div>
            </ComboboxOption>
          ))}
        {[...(blogsQuery.data || [])].map((blog: BlogPost) => {
          const { attributes } = blog;
          const { title, contributor, createdAt, description, thumbnailImage } = attributes;


          const thumbnailImageUrl = thumbnailImage?.data?.attributes.url || ImagesBlogBlogThumbnailJpeg.src;
          const thumbnailImageAlt = thumbnailImage?.data?.attributes.alternativeText || 'Blog Thumbnail';
          const blogTitle = title;
          const blogContributor = contributor?.data?.attributes?.name || '';
          const blogCreatedAt = createdAt;
          const blogDescription = description;
          const blogSlug = blog.attributes.slug;
          return (
            <ComboboxOption
              key={blog.id}
              value={clsx(`/blog/${blogSlug}`)}
              className="tw-flex tw-items-start tw-p-4 tw-gap-4 tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-b last:tw-border-none tw-border-gray-200 tw-cursor-pointer hover:tw-bg-blue-50"
            >
              <div className="tw-w-16 tw-h-16 tw-overflow-hidden tw-rounded-md tw-flex-shrink-0">
                <Image
                  src={thumbnailImageUrl}
                  alt={thumbnailImageAlt}
                  width={64}
                  height={64}
                  className="tw-w-full tw-h-full tw-object-cover"
                />
              </div>
              <div className="tw-max-w-80 tw-truncate tw-text-left">
                <h3 className="tw-font-semibold tw-text-base tw-text-gray-800 max-nine:tw-text-sm">
                  {blogTitle}
                </h3>
                <p className="tw-text-sm tw-text-gray-500 max-nine:tw-text-xs">
                  {blogContributor} •{' '}
                  {new Date(blogCreatedAt).toLocaleDateString()}
                </p>
                <p className="tw-text-sm tw-text-gray-600 tw-mt-1 tw-line-clamp-2 max-nine:tw-text-xs">
                  {blogDescription}
                </p>
              </div>
            </ComboboxOption>
          )
        })}
      </ComboboxOptions>
    </Combobox>
  );
}
